import { useState } from 'react';
import { Loader2, Search, RefreshCw, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI, smoobuAPI, uplistingAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

export function PropertyCurrencyAuditPanel() {
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [results, setResults] = useState(null);

  const runAudit = async () => {
    setLoading(true);
    setResults(null);
    try {
      const { data } = await diagnosticsAPI.propertyCurrencyAudit();
      setResults(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const resyncAndReAudit = async () => {
    setSyncing(true);
    try {
      await Promise.all([
        smoobuAPI.syncProperties(),
        uplistingAPI.syncProperties(),
      ]);
      toast.success('Property sync complete, re-running audit...');
      const { data } = await diagnosticsAPI.propertyCurrencyAudit();
      setResults(data);
      if (data.summary.mismatches === 0) {
        toast.success('All currency mismatches resolved');
      } else {
        toast.warning(`${data.summary.mismatches} mismatch(es) remain after re-sync`);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSyncing(false);
    }
  };

  const summary = results?.summary;
  const properties = results?.properties ?? [];
  const hasMismatches = summary?.mismatches > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          Property Currency Audit
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Checks whether each portfolio property's stored currency matches its
          region's expected currency (UK = GBP, FR = EUR). A mismatch causes the
          KPI and YTD revenue figures to diverge.
        </p>

        <div className="flex items-center gap-3">
          <Button onClick={runAudit} disabled={loading || syncing}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Run Audit
          </Button>
        </div>

        {results && (
          <div className="space-y-4">
            {/* Summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Total properties</p>
                <p className="text-xl font-bold tabular-nums">{summary.total}</p>
              </div>
              <div className={`rounded-lg border p-3 ${hasMismatches ? 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950' : 'border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-950'}`}>
                <p className="text-xs text-muted-foreground">Mismatches</p>
                <p className={`text-xl font-bold tabular-nums ${hasMismatches ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                  {summary.mismatches}
                </p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">UK properties</p>
                <p className="text-xl font-bold tabular-nums">{summary.uk_properties}</p>
                {summary.uk_with_eur > 0 && (
                  <p className="text-xs text-red-500 mt-0.5">{summary.uk_with_eur} with non-GBP</p>
                )}
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">FR properties</p>
                <p className="text-xl font-bold tabular-nums">{summary.fr_properties}</p>
                {summary.fr_with_gbp > 0 && (
                  <p className="text-xs text-red-500 mt-0.5">{summary.fr_with_gbp} with non-EUR</p>
                )}
              </div>
            </div>

            {/* No mismatches message */}
            {!hasMismatches && (
              <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 font-medium">
                <CheckCircle2 className="h-4 w-4" />
                All properties have the expected currency for their region.
              </div>
            )}

            {/* Property table */}
            <div className="space-y-2">
              <p className="text-sm font-medium">All properties</p>
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-3 py-2 font-medium">Property</th>
                      <th className="text-left px-3 py-2 font-medium">Region</th>
                      <th className="text-left px-3 py-2 font-medium">Platform</th>
                      <th className="text-left px-3 py-2 font-medium">Stored Currency</th>
                      <th className="text-left px-3 py-2 font-medium">Expected</th>
                      <th className="text-left px-3 py-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {properties.map((p) => {
                      const ok = p.currency_matches_region_expected;
                      return (
                        <tr
                          key={`${p.platform}-${p.property_id}`}
                          className={`border-b last:border-0 ${!ok ? 'bg-red-50/50 dark:bg-red-950/30' : ''}`}
                        >
                          <td className="px-3 py-2">
                            <div className="font-medium">{p.name}</div>
                            <div className="text-xs text-muted-foreground font-mono">{p.property_id}</div>
                          </td>
                          <td className="px-3 py-2">{p.region.toUpperCase()}</td>
                          <td className="px-3 py-2 text-xs text-muted-foreground">{p.platform}</td>
                          <td className="px-3 py-2">
                            <Badge variant={ok ? 'secondary' : 'destructive'}>
                              {p.stored_currency}
                            </Badge>
                            {p.raw_currency_field == null && (
                              <span className="text-xs text-muted-foreground ml-1">(default)</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">{p.expected_currency}</td>
                          <td className="px-3 py-2">
                            {ok ? (
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                            ) : (
                              <AlertTriangle className="h-4 w-4 text-red-500" />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Re-sync button (only when mismatches exist) */}
            {hasMismatches && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950 p-3">
                <p className="text-sm font-medium mb-2">
                  {summary.mismatches} property/properties have a currency that does not
                  match the expected value for their region. Re-syncing properties from
                  Smoobu/Uplisting may correct stored currency fields.
                </p>
                <Button
                  variant="outline"
                  onClick={resyncAndReAudit}
                  disabled={syncing || loading}
                >
                  {syncing ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Re-sync All Properties
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
